/**
 *  AenaPointDetail script file
**/

import { mapActions, mapGetters } from 'vuex'
import { ACTIONS, GETTERS } from '@/store'
import setLocation from '@/mixins/setLocation'
import capitalize from '@/filters/capitalize'
import { sanitizeHTML } from '@/utils/sanitizeHTML'
import { normalizeLowerCase } from '@/utils/matchString'
import setDeviceWidthClass from '@/mixins/setDeviceWidthClass'
import durationTime from '@/filters/durationTime'
import saveActions from '@/mixins/saveActions'

export default {
  name: 'app-aenaPointDetail',
  mixins: [setLocation, capitalize, setDeviceWidthClass, durationTime, saveActions],
  components: {},
  props: {
    headerHeight: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      placeholder: '',
      header: 0,
      pagination: 0,
      isCollapsed: false,
      debugMode: process.env.VUE_APP_DEBUG_MODE === 'true'
    }
  },
  mounted () {
    if (this.isMupiMode && this.poiId === this.poi.properties.id) {
      this.calculateRoute()
      return
    }
    const floor = this.$t('FLOOR')
    this.placeholder = `${this.location.name}. ${floor.charAt(0).toUpperCase() + floor.slice(1)} ${this.floor}`
    this.$refs.title.focus()

    setTimeout(() => {
      this.header = this.$refs.title.getBoundingClientRect().height
      this.pagination = this.$refs.pagination ? this.$refs.pagination.$el.getBoundingClientRect().height + 10 : 0

      this.$nextTick(() => this.$refs.scrollbar.update())
    }, 260)
  },
  destroyed () {
    this[ACTIONS.CLEAR_POI]()
  },
  computed: {
    ...mapGetters({
      isMupiMode: GETTERS.IS_MUPI_MODE,
      poiId: GETTERS.GET_POI_ID,
      poiDId: GETTERS.GET_POID_ID,
      isMobile: GETTERS.GET_IS_MOBILE,
      poi: GETTERS.GET_POI,
      poisGrouped: GETTERS.GET_POI_GROUPED,
      poisCatalog: GETTERS.GET_POIS_CATALOG,
      airportID: GETTERS.GET_AIRPORT_ID,
      airport: GETTERS.GET_AIRPORT
    }),
    attrs () {
      return this.poi.properties.attrs || {}
    },
    title () {
      return this.$t(this.attrs.pat_nombre)
    },
    image () {
      let url = (this.attrs.pat_imagen_marca_0 || this.attrs.pat_imagen_marca_1)

      if (url && !url.includes(process.env.VUE_APP_AENA_HOST)) {
        url = process.env.VUE_APP_AENA_HOST + url
      }

      return url
    },
    icon () {
      return this.poi.properties.icon
    },
    description () {
      return sanitizeHTML(this.attrs.pat_descripcion_0 || this.attrs.pat_descripcion_1 || this.observations)
    },
    subcategories () {
      return this.poi.properties.subcategories.reduce((acum, current) => {
        this.poisCatalog.some(c => {
          const x = c.subcategories.find(s => s.id === current)
          if (x) {
            acum.push(x)
          }
          return !!x
        })
        return acum
      }, [])
    },
    phone () {
      return sanitizeHTML(this.attrs.pat_telefono_0 || this.attrs.pat_telefono_1)
    },
    mail () {
      return sanitizeHTML(this.attrs.pat_email)
    },
    web () {
      return sanitizeHTML(this.attrs.pat_enlace_0 || this.attrs.pat_enlace_1)
    },
    moreInformation () {
      let url = this.attrs.pat_url

      if (url && !url.includes(process.env.VUE_APP_AENA_HOST)) {
        url = process.env.VUE_APP_AENA_HOST + url
      }

      return url
    },
    time () {
      return sanitizeHTML(this.attrs.pat_horario_0 || this.attrs.pat_horario_1)
    },
    floor () {
      return this.poi.properties.levelName
    },
    location () {
      this.setLocation([this.poi])
      return this.poi.properties.location
    },
    showDescriptionBlock () {
      return this.image || this.description || this.subcategories.length || this.phone || this.mail || this.web || this.time || this.observations
    },
    tagLabel () {
      return `${this.poi.properties.location.name}. Planta ${this.poi.properties.levelName}. ${this.$t(this.poi.properties.attrs.pat_nombre)}`
    },
    lastWait () {
      return this.poi.properties.lastWait * 2
    },
    hasRoute () {
      return this.airport.has_graph
    },
    poiID () {
      return this.poi.properties.id
    },
    coordinates () {
      // LAT/LON to match the GeoJSON specification.
      const lat = Number(this.poi.geometry.coordinates[1].toFixed(5))
      const lon = Number(this.poi.geometry.coordinates[0].toFixed(5))
      return [lat, lon]
    },
    observations () {
      return this.attrs.pat_observaciones
    }
  },
  methods: {
    ...mapActions([
      ACTIONS.LOAD_POI,
      ACTIONS.CLEAR_POI
    ]),
    onPaginate (items) {
      const item = items[0]
      if (item.properties.id !== this.poi.properties.id) {
        this[ACTIONS.LOAD_POI]({ poi: item, pointGrouped: this.poisGrouped })
      }
    },
    filterBySubcategory (subcategory) {
      this.$emit('onSelect', { component: 'aena-points-categories', subcategories: [ subcategory ] })
    },
    goBack () {
      if (this.isMobile) {
        this.$emit('goBack')
      } else {
        this.$emit('onSelect', { component: 'aena-points-categories' })
      }
    },
    calculateRoute () {
      this.$emit('onSelect', { pointDetail: this.poi, component: 'aena-route-pre' })

      if (this.poiDId) {
        const poi = this.findPoi(this.poiDId)
        this.$emit('onSelect', { pointDetail: poi, component: 'aena-route-pre' })
      }
    },
    expand () {
      this.isCollapsed = !this.isCollapsed
      this.$emit('onCollapsed', this.isCollapsed)

      window.setTimeout(() => {
        this.header = this.$refs.title.getBoundingClientRect().height
        this.pagination = this.$refs.pagination ? this.$refs.pagination.$el.getBoundingClientRect().height + 10 : 0
        this.$refs.scrollbar.update()
      }, 260)
    },
    visitWeb (url) {
      const tag = {
        'eventCategory': 'buscador',
        'eventAction': 'visitar web',
        'eventLabel': normalizeLowerCase(this.tagLabel),
        'language': this.$i18n.locale,
        'airportID': this.airportID
      }
      window.digitalChange('event', tag)

      window.open(url, '_blank')
    },
    sendEmail (mail) {
      const tag = {
        'eventCategory': 'buscador',
        'eventAction': 'enviar email',
        'eventLabel': normalizeLowerCase(this.tagLabel),
        'language': this.$i18n.locale,
        'airportID': this.airportID
      }
      window.digitalChange('event', tag)

      window.location.href = `mailto:${mail}`
    },
    moreInfo (url) {
      const tag = {
        'eventCategory': 'buscador',
        'eventAction': 'mas informacion',
        'eventLabel': normalizeLowerCase(this.tagLabel),
        'language': this.$i18n.locale,
        'airportID': this.airportID
      }
      window.digitalChange('event', tag)

      window.open(url, '_blank')
    }
  },
  watch: {
    isMobile: {
      immediate: true,
      handler (value) {
        this.isCollapsed = value
      }
    }
  }
}
